import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { ListSearchField, useSearchableRows } from "@/components/ListSearchField";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  warehousesApi, warehouseLocationsApi,
  type WarehouseLocationDto, type WarehouseLocationPayload, type LocationLevel,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";

const LEVELS: LocationLevel[] = ["Zone", "Aisle", "Rack", "Bin"];
const LEVEL_COLOR: Record<LocationLevel, "default" | "info" | "warning" | "success"> = {
  Zone: "default", Aisle: "info", Rack: "warning", Bin: "success",
};

export function WarehouseLocationsPage() {
  const qc = useQueryClient();
  const [warehouseId, setWarehouseId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WarehouseLocationDto | null>(null);
  const canManage = useAuth(s => s.hasPermission("inventory.location.manage"));

  const warehouses = useQuery({ queryKey: ["warehouses"], queryFn: () => warehousesApi.list({ pageSize: 200 }), staleTime: 5 * 60_000 });

  const query = useQuery({
    queryKey: ["warehouse-locations", warehouseId],
    queryFn: () => warehouseLocationsApi.list({ warehouseId, pageSize: 500 }),
    enabled: !!warehouseId,
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Location removed" },
    mutationFn: (id: string) => warehouseLocationsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouse-locations"] }),
  });

  // Parent lookup spans the whole warehouse, not the filtered view — a bin's parent rack has to
  // resolve even when the search has narrowed the rack itself out of the table.
  const allRows = query.data?.items ?? [];
  const codeById = useMemo(() => new Map(allRows.map(r => [r.id, r.code])), [allRows]);
  const { search, setSearch, rows } = useSearchableRows(
    allRows, r => `${r.code} ${r.name} ${r.level} ${(r.parentId && codeById.get(r.parentId)) || ""}`);

  return (
    <Box>
      <PageHeader breadcrumbs={["Administration"]} title="Warehouse bins & locations"
        actions={<>{canManage && warehouseId && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setDialogOpen(true); }}>
            New location
          </Button>
        )}</>}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField select label="Warehouse" size="small" value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)} sx={{ minWidth: 320 }}>
          <MenuItem value=""><em>— Pick a warehouse —</em></MenuItem>
          {warehouses.data?.items.map(w => <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>)}
        </TextField>
      </Paper>

      {!warehouseId && <Alert severity="info">Pick a warehouse to manage its zones, aisles, racks and bins.</Alert>}
      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
      {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}

      {warehouseId && (
        <Paper>
          <Box sx={{ p: 2, pb: 0 }}>
            <ListSearchField value={search} onChange={setSearch}
              placeholder="Search by code, name, level, or parent" fullWidth />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="center">Level</TableCell>
                  <TableCell>Parent</TableCell>
                  {canManage && <TableCell align="right" width={96}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell><b>{r.code}</b></TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell align="center"><Chip size="small" color={LEVEL_COLOR[r.level]} label={r.level} /></TableCell>
                    <TableCell>{r.parentId ? (codeById.get(r.parentId) ?? "—") : "—"}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title="Edit"><IconButton size="small"
                          onClick={() => { setEditing(r); setDialogOpen(true); }}>
                          <EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error"
                          onClick={() => { if (confirm(`Delete location ${r.code}?`)) deleteMutation.mutate(r.id); }}>
                          <DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {rows.length === 0 && !query.isLoading && (
                  <TableRow><TableCell colSpan={5}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      {search
                        ? `No location matches "${search}".`
                        : "No locations yet. Add a Zone, then Aisles/Racks/Bins under it."}
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {warehouseId && (
        <LocationDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
          warehouseId={warehouseId} row={editing} options={rows} />
      )}
    </Box>
  );
}

function LocationDialog({
  open, onClose, warehouseId, row, options,
}: {
  open: boolean; onClose: () => void; warehouseId: string;
  row: WarehouseLocationDto | null; options: WarehouseLocationDto[];
}) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [level, setLevel] = useState<LocationLevel>("Bin");
  const [parentId, setParentId] = useState("");

  useEffect(() => {
    if (!open) return;
    setCode(row?.code ?? ""); setName(row?.name ?? "");
    setLevel(row?.level ?? "Bin"); setParentId(row?.parentId ?? "");
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Location updated" : "Location created" },
    mutationFn: async () => {
      const payload: WarehouseLocationPayload = {
        warehouseId, parentId: parentId || null, code: code.trim(), name: name.trim(), level,
      };
      if (isEdit && row) return warehouseLocationsApi.update(row.id, payload);
      return warehouseLocationsApi.create(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["warehouse-locations"] }); onClose(); },
  });

  const parents = options.filter(o => o.id !== row?.id);
  const canSubmit = code.trim() && name.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit location" : "New location"}</ClosableDialogTitle>
      <DialogContent dividers>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField label="Code *" size="small" fullWidth value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
            <TextField select label="Level *" size="small" fullWidth value={level}
              onChange={(e) => setLevel(e.target.value as LocationLevel)}>
              {LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="Name *" size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField select label="Parent location" size="small" fullWidth value={parentId}
            onChange={(e) => setParentId(e.target.value)} helperText="Optional — e.g. a Bin inside a Rack">
            <MenuItem value=""><em>None (top level)</em></MenuItem>
            {parents.map(p => <MenuItem key={p.id} value={p.id}>{p.code} — {p.name} ({p.level})</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => mutation.mutate()}>
          {isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
